import {
  collection,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  getCachedNews,
  loadNewsWithCache,
  newsCacheKeys,
} from './newsCache';

const getNewsTimestamp = (news) => {
  if (typeof news.publishedAt?.toMillis === 'function') {
    return news.publishedAt.toMillis();
  }

  if (Number.isFinite(news.publishedAt?.seconds)) {
    return news.publishedAt.seconds * 1000;
  }

  if (typeof news.timestamp?.toMillis === 'function') {
    return news.timestamp.toMillis();
  }

  if (Number.isFinite(news.timestamp?.seconds)) {
    return news.timestamp.seconds * 1000;
  }

  const parsed = Date.parse(
    `${news.year}-${news.month}-${news.day}T${news.time || '00:00'}`
  );
  return Number.isNaN(parsed) ? 0 : parsed;
};

const sortAuthorNews = (news) => [...news].sort(
  (a, b) => getNewsTimestamp(b) - getNewsTimestamp(a)
);

const snapshotToNews = (snapshot) =>
  snapshot.docs.map((newsDocument) => ({
    id: newsDocument.id,
    ...newsDocument.data(),
  }));

export const getAuthorNewsCacheKey = (uid, username) =>
  newsCacheKeys.author(uid, username, 'all');

export const getCachedAuthorNews = (uid, username) =>
  getCachedNews(getAuthorNewsCacheKey(uid, username));

export const loadAuthorNews = (uid, username) => {
  const cacheKey = getAuthorNewsCacheKey(uid, username);

  return loadNewsWithCache(cacheKey, async () => {
    const startedAt = performance.now();

    if (process.env.NODE_ENV === 'development') {
      console.debug('[PROFILE NEWS FIRESTORE START]', {
        uid,
        username,
        time: startedAt,
      });
    }

    const requests = [];

    if (uid) {
      requests.push(getDocs(query(
        collection(db, 'news'),
        where('authorId', '==', uid)
      )));
    }

    if (username) {
      requests.push(getDocs(query(
        collection(db, 'news'),
        where('author', '==', username)
      )));
    }

    const snapshots = await Promise.all(requests);
    const uniqueNews = new Map();

    snapshots.flatMap(snapshotToNews).forEach((news) => {
      uniqueNews.set(news.id, news);
    });

    const articles = sortAuthorNews([...uniqueNews.values()]);

    if (process.env.NODE_ENV === 'development') {
      console.debug('[PROFILE NEWS FIRESTORE COMPLETE]', {
        uid,
        username,
        count: articles.length,
        duration: performance.now() - startedAt,
      });
    }

    return articles;
  });
};
